// Copyright ©️ 2026 eVolve MEP, LLC

import React, { useCallback, useEffect, useRef, useState } from 'react';

type OverlayScrollbarProps = {
  className?: string;
  style?: React.CSSProperties;
  children: React.ReactNode;
  scrollbarWidth?: number;
};

export const OverlayScrollbar = ({ className, style, children, scrollbarWidth = 8 }: OverlayScrollbarProps) => {
  const scrollRef = useRef<HTMLDivElement>(null);

  const [showScrollbar, setShowScrollbar] = useState(false);
  const [scrollTop, setScrollTop] = useState(0);
  const [thumbHeight, setThumbHeight] = useState(0);
  const [hovered, setHovered] = useState(false);
  const [dragging, setDragging] = useState(false);

  const handleScrollAndResize = useCallback(() => {
    const target = scrollRef.current;
    if (!target) return;
    setScrollTop(target.scrollTop);

    const visible = target.clientHeight < target.scrollHeight;
    setShowScrollbar(visible);

    if (visible) {
      // Calculate thumb height proportionally
      const height = target.clientHeight;
      const scrollHeight = target.scrollHeight;
      setThumbHeight(Math.max((height / scrollHeight) * height, 20));
    }
  }, []);

  // Listen to scrolling and resizing
  useEffect(() => {
    handleScrollAndResize();
    const handleResize = () => handleScrollAndResize();
    window.addEventListener('resize', handleResize);
    const ro = new ResizeObserver(handleResize);
    if (scrollRef.current) ro.observe(scrollRef.current);
    return () => {
      window.removeEventListener('resize', handleResize);
      ro.disconnect();
    };
  }, [handleScrollAndResize]);

  const onThumbDrag = useCallback(
    (e: React.MouseEvent) => {
      const target = scrollRef.current;
      if (!target) return;
      setDragging(true);
      e.preventDefault();
      const startY = e.clientY;
      const startScroll = target.scrollTop;

      const mouseMoveHandler = (moveEvent: MouseEvent) => {
        const delta = moveEvent.clientY - startY;
        const ratio = (target.scrollHeight - target.clientHeight) / (target.clientHeight - thumbHeight);
        target.scrollTop = startScroll + delta * ratio;
      };
      const mouseUpHandler = () => {
        window.removeEventListener('mousemove', mouseMoveHandler);
        window.removeEventListener('mouseup', mouseUpHandler);
        // setDragging(false);
      };

      window.addEventListener('mousemove', mouseMoveHandler);
      window.addEventListener('mouseup', mouseUpHandler);
    },
    [thumbHeight],
  );

  // Calculate thumb top
  const content = scrollRef.current;
  const thumbTop = content
    ? (scrollTop / (content.scrollHeight - content.clientHeight)) * (content.clientHeight - thumbHeight)
    : 0;

  return (
    <div
      className={className}
      style={{ position: 'relative', ...style, overflow: 'hidden' }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div
        ref={scrollRef}
        style={{
          height: '100%',
          width: '100%',
          overflowY: 'scroll',
          scrollbarWidth: 'none',
        }}
        onScroll={handleScrollAndResize}
      >
        {children}
      </div>

      {showScrollbar && (
        // Scrollbar thumb
        <div
          style={{
            position: 'absolute',
            top: 0,
            right: 0,
            width: scrollbarWidth,
            height: '100%',
            background: 'transparent',
            pointerEvents: 'none',
          }}
        >
          <div
            className={`nav-container-thumb ${hovered ? 'visible' : ''} ${dragging ? 'active' : ''}`}
            onMouseDown={onThumbDrag}
            style={{
              position: 'absolute',
              right: 0,
              width: scrollbarWidth,
              top: thumbTop,
              height: thumbHeight,
              backgroundColor: 'var(--mantine-color-app-shell-gray-4)',
              borderRadius: 4,
              pointerEvents: 'all',
            }}
          ></div>
        </div>
      )}
    </div>
  );
};
